import { Router } from '@angular/router';
import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AlertsService } from '../services/alerts.service';
import { alertRoute } from '../services/alert-deep-link';
import { Alert, AlertSummary } from '../models/alert';

/**
 * Header bell: open-alert count badge (distinct style when critical alerts
 * exist) + dropdown with the latest open alerts and quick acknowledge.
 */
@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  static readonly DROPDOWN_ALERT_COUNT = 5;

  summary: AlertSummary | null = null;
  dropdownOpen = false;
  recentAlerts: Alert[] = [];
  loadingAlerts = false;

  private destroyed$ = new Subject<void>();

  constructor(
    private router: Router,
    private alertsService: AlertsService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.alertsService.summary$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((summary) => (this.summary = summary));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.dropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  get openCount(): number {
    return this.summary?.openCount ?? 0;
  }

  get hasCritical(): boolean {
    return (this.summary?.criticalCount ?? 0) > 0;
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.loadRecentAlerts();
    }
  }

  loadRecentAlerts(): void {
    this.loadingAlerts = true;
    this.alertsService
      .getAlerts('open', undefined, NotificationBellComponent.DROPDOWN_ALERT_COUNT)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((alerts) => {
        this.recentAlerts = alerts;
        this.loadingAlerts = false;
      });
  }

  acknowledge(alert: Alert, event: MouseEvent): void {
    event.stopPropagation();
    this.alertsService
      .acknowledge(alert.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.loadRecentAlerts());
  }

  severityIcon(alert: Alert): string {
    switch (alert.severity) {
      case 'critical':
        return 'fa-circle-exclamation';
      case 'warning':
        return 'fa-triangle-exclamation';
      default:
        return 'fa-circle-info';
    }
  }

  /**
   * One-click into the project that fired an alert.
   *
   * Navigates by the canonical deep link, so following an alert in-app lands in
   * exactly the same place as clicking the emailed version of the same link.
   * Silently does nothing for an alert with no project — the row hides its
   * affordance in that case, and a click that quietly goes nowhere beats one
   * that navigates somewhere wrong.
   */
  openAlertProject(alert: Alert): void {
    const route = alertRoute(alert);
    if (!route) {
      return;
    }
    void this.router.navigate([route.path], { queryParams: route.queryParams });
  }

  /** True when this alert can be followed to a project. */
  canOpenAlert(alert: Alert): boolean {
    return alertRoute(alert) !== null;
  }
}
